<script>
    import { fade } from 'svelte/transition';
    import { push } from 'svelte-spa-router';
    import { onMount } from 'svelte';
    import Header from '../components/Header.svelte';
    import TopInfo from '../components/TopInfo.svelte';
    import Preloader from '../components/Preloader.svelte';
    import UserStore from '../stores/user';
    import fetchData from '../api.js';

    const PURCHASE_ITEMS_ACTION = 'purchase_items';
    const PURCHASE_ITEMS_CREATE_ACTION = 'crm_create_purchase_item';
    const PURCHASE_ITEMS_UPDATE_ACTION = 'crm_update_purchase_item';

    let isLoading = false;
    let loadError = '';
    let serverItems = [];
    let localItems = [];
    let items = [];
    let searchTerm = '';
    let uploadError = '';
    let uploadSuccess = '';
    let uploadedFileName = '';
    let manualName = '';
    let manualSku = '';
    let manualStock = '';
    let manualWeight = '';
    let editingItemId = null;
    let editDraft = {
        name: '',
        sku: '',
        stock: '',
        weight: ''
    };

    onMount(() => {
        if (!$UserStore || !$UserStore.data) {
            push('/login');
            return;
        }

        fetchItems();
    });

    function normalizeStock(rawItem) {
        const rawStock = rawItem.stock ?? rawItem.inventory ?? rawItem.quantity ?? rawItem.available ?? rawItem.balance ?? '';
        const stockNumber = Number(rawStock);

        if (Number.isFinite(stockNumber)) {
            return stockNumber;
        }

        return rawStock === '' ? '-' : rawStock;
    }

    function normalizeWeight(rawItem) {
        const rawWeight = rawItem.weight ?? rawItem.item_weight ?? rawItem.weight_kg ?? rawItem.kg ?? rawItem.mass ?? '';
        const weightNumber = Number(rawWeight);

        if (Number.isFinite(weightNumber)) {
            return weightNumber;
        }

        return rawWeight === '' ? 0 : rawWeight;
    }

    function normalizeItem(rawItem, index) {
        return {
            id: rawItem.id || rawItem.item_id || `item-${index}`,
            name: (rawItem.name || rawItem.product_name || rawItem.description_product || rawItem.title || '-').toString(),
            sku: (rawItem.sku || rawItem.product_sku || rawItem.makat || rawItem.sku_code || '-').toString(),
            stock: normalizeStock(rawItem),
            weight: normalizeWeight(rawItem)
        };
    }

    function buildLocalKey(item) {
        return `${item.name.toString().trim().toLowerCase()}__${item.sku.toString().trim().toLowerCase()}`;
    }

    function mergeUniqueItems(baseItems, newItems) {
        const mergedMap = new Map(baseItems.map((item) => [buildLocalKey(item), item]));

        newItems.forEach((item) => {
            mergedMap.set(buildLocalKey(item), item);
        });

        return Array.from(mergedMap.values());
    }

    async function fetchItems() {
        isLoading = true;
        loadError = '';

        try {
            const response = await fetchData({
                action: PURCHASE_ITEMS_ACTION,
                token: $UserStore.data
            });

            if (!response) {
                loadError = 'אירעה שגיאה בזמן טעינת פריטי הרכש';
                serverItems = [];
                return;
            }

            if (response.error || response.success === 0) {
                loadError = response.msg || response.error || 'טעינת פריטי הרכש נכשלה';
                serverItems = [];
                return;
            }

            const rawItems = Array.isArray(response.data)
                ? response.data
                : Array.isArray(response.items)
                    ? response.items
                    : [];

            serverItems = rawItems
                .filter((item) => {
                    const isActive = item.is_active ?? item.active ?? item.isActive ?? 1;
                    return Number(isActive) !== 0;
                })
                .map((item, index) => normalizeItem(item, index));
        } catch (error) {
            loadError = error.message || 'אירעה שגיאה בזמן טעינת פריטי הרכש';
            serverItems = [];
        } finally {
            isLoading = false;
        }
    }

    function normalizeUploadedItem(item, index) {
        return normalizeItem({
            id: item.id || `upload-${Date.now()}-${index}`,
            name: item.name || item.product_name || item.description_product || item.title || '',
            sku: item.sku || item.product_sku || item.makat || item.sku_code || '',
            stock: item.stock ?? item.inventory ?? item.quantity ?? item.available ?? item.balance ?? '',
            weight: item.weight ?? item.item_weight ?? item.weight_kg ?? item.kg ?? item.mass ?? ''
        }, index);
    }

    function parseCsvContent(content) {
        const lines = content
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter((line) => line !== '');

        if (!lines.length) {
            throw new Error('הקובץ ריק');
        }

        const headers = lines[0].split(';').map((header) => header.trim().toLowerCase());
        const nameIndex = headers.findIndex((header) => ['name', 'product_name', 'description_product', 'title', 'שם'].includes(header));
        const skuIndex = headers.findIndex((header) => ['sku', 'product_sku', 'makat', 'sku_code', 'מקט'].includes(header));
        const stockIndex = headers.findIndex((header) => ['stock', 'inventory', 'quantity', 'available', 'balance', 'מלאי'].includes(header));
        const weightIndex = headers.findIndex((header) => ['weight', 'item_weight', 'weight_kg', 'kg', 'mass', 'משקל', 'משקל_קג', 'קג'].includes(header));

        if (nameIndex === -1 || skuIndex === -1 || stockIndex === -1) {
            throw new Error('CSV חייב לכלול עמודות שם, מקט ומלאי');
        }

        return lines.slice(1).map((line, index) => {
            const cols = line.split(';').map((col) => col.trim());

            return normalizeUploadedItem({
                name: cols[nameIndex] ?? '',
                sku: cols[skuIndex] ?? '',
                stock: cols[stockIndex] ?? '',
                weight: weightIndex >= 0 ? (cols[weightIndex] ?? '') : ''
            }, index);
        });
    }

    function startEditItem(item) {
        editingItemId = item.id;
        editDraft = {
            name: item.name || '',
            sku: item.sku || '',
            stock: item.stock || '',
            weight: item.weight || ''
        };
        uploadError = '';
        uploadSuccess = '';
    }

    function cancelItemEdit() {
        editingItemId = null;
        editDraft = {
            name: '',
            sku: '',
            stock: '',
            weight: ''
        };
        uploadError = '';
        uploadSuccess = '';
    }

    async function saveItemRowEdit() {
        uploadError = '';
        uploadSuccess = '';

        const payload = {
            id: editingItemId,
            name: editDraft.name.toString().trim(),
            sku: editDraft.sku.toString().trim(),
            stock: editDraft.stock.toString().trim(),
            weight: editDraft.weight.toString().trim()
        };

        if (!payload.id || !payload.name || !payload.sku) {
            uploadError = 'יש למלא שם ומקט עבור המוצר';
            return;
        }

        isLoading = true;

        try {
            const response = await fetchData({
                action: PURCHASE_ITEMS_UPDATE_ACTION,
                token: $UserStore.data,
                items: [payload]
            });

            if (!response) {
                uploadError = 'אירעה שגיאה בזמן עדכון המוצר';
                return;
            }

            if (response.error || response.success === 0) {
                uploadError = response.msg || response.error || 'עדכון המוצר נכשל';
                return;
            }

            uploadSuccess = response.msg || 'המוצר עודכן בהצלחה';
            editingItemId = null;
            editDraft = {
                name: '',
                sku: '',
                stock: '',
                weight: ''
            };
            await fetchItems();
        } catch (error) {
            uploadError = error.message || 'אירעה שגיאה בזמן עדכון המוצר';
        } finally {
            isLoading = false;
        }
    }

    async function deleteItem(item) {
        if (!item || !item.id) {
            return;
        }

        uploadError = '';
        uploadSuccess = '';
        isLoading = true;

        try {
            const response = await fetchData({
                action: PURCHASE_ITEMS_UPDATE_ACTION,
                token: $UserStore.data,
                items: [{
                    id: item.id,
                    name: item.name || '',
                    sku: item.sku || '',
                    stock: item.stock || 0,
                    weight: item.weight || 0,
                    is_active: 0
                }]
            });

            if (!response) {
                uploadError = 'אירעה שגיאה בזמן מחיקת המוצר';
                return;
            }

            if (response.error || response.success === 0) {
                uploadError = response.msg || response.error || 'מחיקת המוצר נכשלה';
                return;
            }

            uploadSuccess = response.msg || 'המוצר נמחק בהצלחה';
            await fetchItems();
        } catch (error) {
            uploadError = error.message || 'אירעה שגיאה בזמן מחיקת המוצר';
        } finally {
            isLoading = false;
        }
    }

    async function addManualProduct() {
        uploadError = '';
        uploadSuccess = '';

        const name = manualName.toString().trim();
        const sku = manualSku.toString().trim();
        const stock = manualStock.toString().trim();
        const weight = manualWeight.toString().trim();

        if (!name || !sku) {
            uploadError = 'יש למלא שם ומקט עבור מוצר ידני';
            return;
        }

        isLoading = true;

        try {
            const payload = {
                sku,
                name,
                stock: stock === '' ? 0 : Number(stock),
                weight: weight === '' ? 0 : Number(weight)
            };

            const response = await fetchData({
                action: PURCHASE_ITEMS_CREATE_ACTION,
                token: $UserStore.data,
                items: [payload]
            });

            if (!response) {
                uploadError = 'אירעה שגיאה בזמן הוספת המוצר';
                return;
            }

            if (response.error || response.success === 0) {
                uploadError = response.msg || response.error || 'הוספת המוצר נכשלה';
                return;
            }

            manualName = '';
            manualSku = '';
            manualStock = '';
            manualWeight = '';
            uploadSuccess = response.msg || 'המוצר נוסף בהצלחה';
            await fetchItems();
        } catch (error) {
            uploadError = error.message || 'אירעה שגיאה בזמן הוספת המוצר';
        } finally {
            isLoading = false;
        }
    }

    function mapItemForUpload(item) {
        const parsedStock = Number(item.stock);
        const parsedWeight = Number(item.weight);

        return {
            sku: item.sku.toString().trim(),
            name: item.name.toString().trim(),
            stock: Number.isFinite(parsedStock) ? parsedStock : 0,
            weight: Number.isFinite(parsedWeight) ? parsedWeight : 0
        };
    }

    async function handleFileUpload(event) {
        uploadError = '';
        uploadSuccess = '';

        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        uploadedFileName = file.name;

        try {
            const fileText = await file.text();
            const lowerName = file.name.toLowerCase();
            let parsedItems = [];

            if (lowerName.endsWith('.json')) {
                const jsonData = JSON.parse(fileText);
                const rawItems = Array.isArray(jsonData)
                    ? jsonData
                    : Array.isArray(jsonData.items)
                        ? jsonData.items
                        : Array.isArray(jsonData.data)
                            ? jsonData.data
                            : [];

                if (!rawItems.length) {
                    throw new Error('לא נמצא מערך פריטים בקובץ JSON');
                }

                parsedItems = rawItems.map((item, index) => normalizeUploadedItem(item, index));
            } else if (lowerName.endsWith('.csv')) {
                parsedItems = parseCsvContent(fileText);
            } else {
                throw new Error('פורמט קובץ לא נתמך. יש להעלות JSON או CSV');
            }

            const validItems = parsedItems.filter((item) => item.name !== '-' && item.sku !== '-');

            if (!validItems.length) {
                throw new Error('לא נמצאו פריטים תקינים בקובץ');
            }

            const uploadPayload = validItems.map(mapItemForUpload);
            const uploadResponse = await fetchData({
                action: PURCHASE_ITEMS_CREATE_ACTION,
                token: $UserStore.data,
                items: uploadPayload
            });

            if (!uploadResponse) {
                throw new Error('אירעה שגיאה בזמן העלאת הפריטים לשרת');
            }

            if (uploadResponse.error || uploadResponse.success === 0) {
                throw new Error(uploadResponse.msg || uploadResponse.error || 'העלאת הפריטים נכשלה');
            }

            const successCount = Number(uploadResponse.data?.success_count ?? validItems.length);
            const failCount = Number(uploadResponse.data?.fail_count ?? 0);

            uploadSuccess = failCount > 0
                ? `${successCount} פריטים הועלו בהצלחה, ${failCount} נכשלו`
                : `${successCount} פריטים הועלו בהצלחה`;

            await fetchItems();
        } catch (error) {
            uploadError = error.message || 'שגיאה בקריאת הקובץ';
        }
    }

    $: items = [...localItems.filter((item) => Number(item.is_active ?? 1) !== 0), ...serverItems];
    $: filteredItems = items.filter((item) => {
        const term = searchTerm.trim().toLowerCase();

        if (!term) {
            return true;
        }

        return item.name.toLowerCase().includes(term) || item.sku.toLowerCase().includes(term);
    });
</script>

<div class="page-container" transition:fade={{ delay: 250, duration: 300 }}>
    <Preloader {isLoading} />
    <Header />

    <div class="page">
        <TopInfo />

        <div class="shipment purchase-items-page">
            <div class="header-row">
                <h1 class="text-[22px] font-bold">פריטי רכש</h1>
                <div class="nav-actions">
                    <button type="button" class="create_button" on:click={() => push('/purchase-orders')}>הזמנת רכש חדשה</button>
                    <button type="button" class="create_button" on:click={() => push('/purchase-orders/locations')}>מיקומי יציאה</button>
                    <button type="button" class="create_button" on:click={() => push('/purchase-orders/history')}>היסטוריית רכש</button>
                </div>
            </div>

            <p class="subtitle">רשימת פריטים מה־endpoint purchase_items עם שם הפריט, מקט, מלאי ומשקל.</p>

            <div class="card filters">
                <div class="filter-grid manual-grid">
                    <div class="filter-item">
                        <label for="manual-item-name">שם פריט</label>
                        <input id="manual-item-name" type="text" bind:value={manualName} placeholder="שם מוצר" />
                    </div>
                    <div class="filter-item">
                        <label for="manual-item-sku">מקט</label>
                        <input id="manual-item-sku" type="text" bind:value={manualSku} placeholder="SKU-100" />
                    </div>
                    <div class="filter-item">
                        <label for="manual-item-stock">מלאי</label>
                        <input id="manual-item-stock" type="text" bind:value={manualStock} placeholder="100" />
                    </div>
                    <div class="filter-item">
                        <label for="manual-item-weight">משקל (ק"ג)</label>
                        <input id="manual-item-weight" type="number" min="0" step="0.01" bind:value={manualWeight} placeholder="2.5" />
                    </div>
                </div>

                <div class="actions upload-actions">
                    <button type="button" class="create_button" on:click={addManualProduct}>הוסף מוצר ידני</button>
                </div>

                {#if uploadError}
                    <div class="error inline-message">{uploadError}</div>
                {/if}

                {#if uploadSuccess}
                    <div class="success inline-message">{uploadSuccess}</div>
                {/if}

                <div class="row-head upload-row">
                    <h2>טעינת קובץ מוצרים</h2>
                    <span class="file-name">{uploadedFileName}</span>
                </div>

                <input type="file" accept=".json,.csv,application/json,text/csv" on:change={handleFileUpload} />

                <p class="helper">
                    CSV חייב לכלול עמודות שם, מקט ומלאי. ניתן להוסיף גם עמודת משקל (למשל: name, sku, stock, weight)
                </p>

                <div class="filter-grid">
                    <div class="filter-item">
                        <label for="purchase-items-search">חיפוש לפי שם או מקט</label>
                        <input id="purchase-items-search" type="text" bind:value={searchTerm} placeholder="לדוגמה: מוצר / SKU" />
                    </div>
                </div>

                <div class="actions">
                    <button type="button" class="create_button" on:click={fetchItems}>רענן רשימה</button>
                </div>

                {#if loadError}
                    <div class="error inline-message">{loadError}</div>
                {/if}
            </div>

            <div class="card">
                {#if !filteredItems.length && !isLoading}
                    <div class="empty">לא נמצאו פריטים להצגה.</div>
                {:else}
                    <div class="table-wrap">
                        <table class="items-table">
                            <thead>
                                <tr>
                                    <th>שם</th>
                                    <th>מקט</th>
                                    <th>מלאי</th>
                                    <th>משקל (ק"ג)</th>
                                    <th>פעולות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each filteredItems as item, itemIndex (item.id + '-' + itemIndex)}
                                    {#if editingItemId === item.id}
                                        <tr>
                                            <td><input bind:value={editDraft.name} class="table-edit-input" /></td>
                                            <td><input bind:value={editDraft.sku} class="table-edit-input" /></td>
                                            <td><input bind:value={editDraft.stock} class="table-edit-input" /></td>
                                            <td><input bind:value={editDraft.weight} class="table-edit-input" /></td>
                                            <td>
                                                <div class="inline-row-actions">
                                                    <button type="button" class="table_action_button" on:click={saveItemRowEdit}>שמור</button>
                                                    <button type="button" class="secondary_button" on:click={cancelItemEdit}>ביטול</button>
                                                </div>
                                            </td>
                                        </tr>
                                    {:else}
                                        <tr>
                                            <td>{item.name}</td>
                                            <td>{item.sku}</td>
                                            <td>{item.stock}</td>
                                            <td>{item.weight || 0}</td>
                                            <td>
                                                <div class="inline-row-actions">
                                                    <button type="button" class="table_action_button" on:click={() => startEditItem(item)}>ערוך</button>
                                                    <button type="button" class="secondary_button" on:click={() => deleteItem(item)}>מחק</button>
                                                </div>
                                            </td>
                                        </tr>
                                    {/if}
                                {/each}
                            </tbody>
                        </table>
                    </div>
                {/if}
            </div>
        </div>
    </div>
</div>

<style>
    .purchase-items-page {
        width: 100%;
        max-width: 1280px;
        padding: 20px;
        box-sizing: border-box;
    }

    .header-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        flex-wrap: wrap;
    }

    .nav-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .subtitle {
        margin-top: 8px;
        color: #5b6475;
    }

    .card {
        background: #fff;
        border-radius: 14px;
        border: 1px solid #eceff5;
        box-shadow: 0 8px 24px rgba(17, 43, 85, 0.08);
        padding: 16px;
        margin-top: 16px;
    }

    .filter-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .manual-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .row-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-top: 14px;
    }

    .row-head h2 {
        margin: 0;
        font-size: 16px;
        font-weight: 700;
    }

    .file-name {
        font-size: 13px;
        color: #667089;
    }

    .helper {
        margin: 8px 0 0;
        font-size: 13px;
        color: #667089;
    }

    .filter-item label {
        display: block;
        font-size: 13px;
        color: #4f5970;
        margin-bottom: 6px;
    }

    .filter-item input {
        width: 100%;
        min-height: 38px;
        border: 1px solid #d6deea;
        border-radius: 8px;
        padding: 0 10px;
        box-sizing: border-box;
    }

    .actions {
        margin-top: 12px;
        display: flex;
        justify-content: flex-end;
    }

    .secondary_button,
    .table_action_button {
        border: 1px solid #dfe8f7;
        background: #f7f9fd;
        color: #2d3a52;
        border-radius: 8px;
        padding: 8px 12px;
        cursor: pointer;
        font-weight: 600;
    }

    .table_action_button {
        padding: 6px 10px;
        font-size: 13px;
    }

    .inline-row-actions {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
    }

    .table-edit-input {
        width: 100%;
        min-height: 32px;
        border: 1px solid #d7dfe9;
        border-radius: 7px;
        padding: 0 8px;
        box-sizing: border-box;
        font-size: 13px;
    }

    .table-wrap {
        width: 100%;
        overflow: auto;
    }

    .items-table {
        width: 100%;
        border-collapse: collapse;
        min-width: 640px;
    }

    .items-table th,
    .items-table td {
        padding: 10px 8px;
        border-bottom: 1px solid #eef2f7;
        vertical-align: middle;
    }

    .items-table th {
        text-align: right;
        color: #3f4760;
        font-size: 13px;
    }

    .empty {
        text-align: center;
        color: #6f7a92;
        padding: 14px;
    }

    .error {
        margin-top: 12px;
        border-radius: 10px;
        padding: 10px 12px;
        font-size: 14px;
        background: #fff2f4;
        color: #b2203f;
        border: 1px solid #ffd6de;
    }

    .success {
        margin-top: 12px;
        border-radius: 10px;
        padding: 10px 12px;
        font-size: 14px;
        background: #edfff4;
        color: #1c8f4a;
        border: 1px solid #caefd9;
    }

    .inline-message {
        margin-top: 10px;
    }

    @media screen and (max-width: 900px) {
        .purchase-items-page {
            padding: 10px;
        }

        .manual-grid {
            grid-template-columns: 1fr;
        }

        .nav-actions {
            width: 100%;
        }

        .nav-actions .create_button {
            flex: 1;
        }
    }
</style>
